import type { WidgetConfig } from "./types";
import type { WidgetModule } from "./widget-module";
import { buildPresets, presetLockedKeys, type WidgetPreset } from "./catalog";

/**
 * An isolated widget registry instance. Create one per consumer
 * ({@link createWidgetRegistry}) and provide it through {@link WidgetRegistryProvider};
 * the kit never owns a process-global. Unknown keys resolve to `undefined` so the
 * runtime renders a graceful placeholder instead of crashing on a removed widget.
 */
export type WidgetRegistry = {
  register<C extends WidgetConfig>(def: WidgetModule<C>): void;
  get(type: string): WidgetModule | undefined;
  list(): WidgetModule[];
  resolveConfig(
    def: WidgetModule | undefined,
    raw: WidgetConfig | null | undefined,
    fromVersion: number,
  ): WidgetConfig;
  /** Add-Card gallery presets, computed lazily + memoized off this instance. */
  getPresets(): WidgetPreset[];
  /** The config keys an instance may not edit — see {@link presetLockedKeys}.
   *  Hand it the RESOLVED config so every caller infers from the same values. */
  lockedKeys(type: string, config: WidgetConfig | null | undefined): ReadonlySet<string>;
};

export type WidgetRegistryOptions = {
  /**
   * Which UNSTAMPED configs (no `WIDGET_PRESET_KEY` at all) may infer their
   * preset from their values — see `inferPreset`. The consumer knows which
   * rows were placed by something other than the gallery (a board seed, a
   * template) and so never received a stamp; a user-placed legacy row is left
   * custom. Default: never infer.
   */
  inferPresetFor?: (type: string, config: WidgetConfig) => boolean;
};

export function createWidgetRegistry(options: WidgetRegistryOptions = {}): WidgetRegistry {
  const modules = new Map<string, WidgetModule<any>>();
  let presetsCache: WidgetPreset[] | null = null;
  const infer = options.inferPresetFor;

  function resolveConfig(
    def: WidgetModule | undefined,
    rawConfig: WidgetConfig | null | undefined,
    fromVersion: number,
  ): WidgetConfig {
    const raw = rawConfig ?? {};
    if (!def?.configSchema) return raw;
    const version = def.schemaVersion ?? 1;
    const migrated =
      def.migrate && fromVersion < version ? def.migrate(raw, fromVersion) : raw;
    const parsed = def.configSchema.safeParse(migrated);
    if (parsed.success) return parsed.data as WidgetConfig;
    // Fall back to schema defaults so a schema with a required field (no
    // `.default()`) keeps the Reset handler safe instead of crashing the editor.
    return schemaDefaults(def.configSchema);
  }

  return {
    register(def) {
      modules.set(def.type, def as WidgetModule<any>);
      presetsCache = null;
    },
    get(type) {
      return modules.get(type);
    },
    list() {
      return [...modules.values()];
    },
    resolveConfig,
    getPresets() {
      if (!presetsCache) presetsCache = buildPresets([...modules.values()]);
      return presetsCache;
    },
    lockedKeys(type, config) {
      return presetLockedKeys(this.getPresets(), type, config, infer);
    },
  };
}

/** `parse({})`-equivalent that never throws: returns the schema defaults, or `{}`
 *  when the schema has no full set of defaults (a missing `.default()`). */
function schemaDefaults(schema: NonNullable<WidgetModule["configSchema"]>): WidgetConfig {
  const parsed = schema.safeParse({});
  return (parsed.success ? parsed.data : {}) as WidgetConfig;
}
